import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';
import { responsiveSpacing } from '../../themes/responsive';
import AlertStats from './alert-components/AlertStats';
import AlertActions from './alert-components/AlertActions';

/**
 * AlertCard component - Alert cards with status transitions
 * Phase 3: Added icon-based actions and status management
 */
const AlertCard = ({
  // Data
  item,
  alertKey,
  
  // Customization
  headerControls = null,
  focusedItemId = null,
  
  // Event handlers
  onStatusChange,
  
  // Utils
  t,
  clientId
}) => {
  const { i18n } = useTranslation();
  
  // Phase 3: Internal state management for status transitions
  const [internalAlertState, setInternalAlertState] = React.useState(() => ({
    technical_problem: item.technical_problem,
    resolution_date: item.resolution_date
  }));
  
  const [actionLoading, setActionLoading] = React.useState(false);

  // Handle status changes with optional metadata
  const handleStatusChange = async (newStatus, metadata = {}) => {
    setActionLoading(true);
    
    try {
      // Update internal state optimistically
      if (newStatus === 'not_broken') {
        setInternalAlertState(prev => ({
          ...prev,
          technical_problem: false
        }));
      } else if (newStatus === 'under_review') {
        setInternalAlertState(prev => ({
          ...prev,
          technical_problem: true,
          planned_fix_date: metadata.planned_fix_date
        }));
      } else if (newStatus === 'broken') {
        setInternalAlertState(prev => ({
          ...prev,
          technical_problem: true
        }));
      } else if (newStatus === 'resolved') {
        setInternalAlertState(prev => ({
          ...prev,
          technical_problem: true,
          resolution_date: metadata.resolution_date
        }));
      }
      
      // Call external handler if provided
      if (onStatusChange) {
        await onStatusChange(newStatus, item.id, metadata);
      }
      
    } catch (error) {
      // Revert state on error
      setInternalAlertState({
        technical_problem: item.technical_problem,
        resolution_date: item.resolution_date,
        planned_fix_date: item.planned_fix_date
      });
    } finally {
      setActionLoading(false);
    }
  };
  
  // Use internal state for rendering
  const currentAlert = {
    ...item,
    ...internalAlertState
  };
  
  // Phase 2: Match ExperimentCard styling exactly
  return (
    <Box
      key={alertKey}
      data-alert-key={alertKey}
      sx={{
        background: `linear-gradient(135deg, ${colors.background.surface} 0%, ${colors.background.subtle} 100%)`,
        border: focusedItemId === item.id 
          ? `2px solid ${colors.action.primary}` 
          : `1px solid ${colors.border.dark}`,
        borderRadius: '12px',
        p: responsiveSpacing.sm,
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 250ms ease-out',
        cursor: 'pointer',
        userSelect: 'none',
        boxShadow: `0 2px 8px ${colors.shadow.soft}`,
        backgroundColor: focusedItemId === item.id 
          ? 'rgba(24, 144, 255, 0.02)' 
          : 'transparent',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 15px ${colors.shadow.soft}`
        },
        '&.status-change-animation': {
          transform: 'scale(1.02)',
          transition: 'transform 0.3s ease-out'
        }
      }}
    >
      {/* Header Controls - Optional controls area at the very top */}
      {headerControls && (
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: '8px',
          mx: '-2px',
        }}>
          {headerControls}
        </Box>
      )}
      
      {/* Alert Stats - Product name and stats in one component */}
      <AlertStats alert={currentAlert} productName={currentAlert.product_name} t={t} />

      {/* Alert Actions - Phase 3: Icon-based actions */}
      <AlertActions 
        alert={currentAlert}
        onStatusChange={handleStatusChange}
        loading={actionLoading}
        t={t}
      />
    </Box>
  );
};

export default AlertCard;